import { ListMusic, Pause, Play, SkipBack, SkipForward, Volume2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useI18n } from '../i18n'
import type { MusicTrack } from '../data/music'

function formatTime(value: number): string {
  if (!Number.isFinite(value) || value < 0) return '0:00'
  const minutes = Math.floor(value / 60)
  const seconds = Math.floor(value % 60).toString().padStart(2, '0')
  return `${minutes}:${seconds}`
}

export function MusicPlayer({ tracks, className = '', compact = false }: { tracks: MusicTrack[]; className?: string; compact?: boolean }) {
  const { locale } = useI18n()
  const audioRef = useRef<HTMLAudioElement>(null)
  const shouldPlayRef = useRef(false)
  const [playing, setPlaying] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(0.72)
  const track = tracks[currentIndex]
  const available = Boolean(track?.src)
  const label = locale === 'zh-CN' ? '音乐播放器' : 'Music player'

  useEffect(() => {
    const audio = audioRef.current
    if (audio) audio.volume = volume
  }, [volume])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    setCurrentTime(0)
    setDuration(0)
    if (shouldPlayRef.current) {
      void audio.play().catch(() => {
        shouldPlayRef.current = false
        setPlaying(false)
      })
    }
  }, [track?.src])

  useEffect(() => () => audioRef.current?.pause(), [])

  const togglePlayback = async () => {
    const audio = audioRef.current
    if (!audio || !available) return
    if (audio.paused) {
      try {
        shouldPlayRef.current = true
        await audio.play()
      } catch {
        shouldPlayRef.current = false
        setPlaying(false)
      }
    } else {
      shouldPlayRef.current = false
      audio.pause()
    }
  }

  const selectTrack = (index: number, keepPlaying = playing) => {
    shouldPlayRef.current = keepPlaying
    if (index === currentIndex) {
      if (keepPlaying && audioRef.current?.paused) void audioRef.current.play()
      return
    }
    setCurrentIndex(index)
  }

  const moveTrack = (direction: -1 | 1, keepPlaying = playing) => {
    if (!tracks.length) return
    selectTrack((currentIndex + direction + tracks.length) % tracks.length, keepPlaying)
  }

  const seek = (value: number) => {
    const audio = audioRef.current
    if (!audio || !duration) return
    audio.currentTime = value
    setCurrentTime(value)
  }

  return (
    <section aria-label={label} className={`border-y border-ink/16 bg-white/95 px-4 py-4 backdrop-blur-xs ${className}`} data-testid="music-player">
      {available && (
        <audio
          ref={audioRef}
          src={track.src}
          autoPlay
          preload="metadata"
          onLoadedMetadata={(event) => setDuration(event.currentTarget.duration || 0)}
          onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
          onPause={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
          onEnded={() => moveTrack(1, true)}
        />
      )}

      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2 text-ink">
          <ListMusic size={13} aria-hidden="true" />
          <p className="truncate text-[9px] font-semibold uppercase tracking-[0.14em]">{locale === 'zh-CN' ? '正在播放' : 'Now playing'}</p>
        </div>
        <span className="font-mono text-[9px] tabular-nums text-ink/55">
          {available ? `${String(currentIndex + 1).padStart(2, '0')} / ${String(tracks.length).padStart(2, '0')}` : '00 / 00'}
        </span>
      </div>

      <div className={`mt-4 grid items-center gap-3 ${compact ? 'grid-cols-[3.25rem_minmax(0,1fr)]' : 'grid-cols-[3.75rem_minmax(0,1fr)]'}`}>
        <div className="grid aspect-square place-items-center border border-ink/16 bg-[#edf3f7] font-serif text-xl text-ink/65" aria-hidden="true">
          {available ? String(currentIndex + 1).padStart(2, '0') : '—'}
        </div>
        <div className="min-w-0">
          <p className="truncate text-[12px] font-semibold text-ink">{track?.title ?? (locale === 'zh-CN' ? '尚未配置可公开音频' : 'No public audio configured')}</p>
          <p className="mt-1 truncate text-[9px] text-ink/58">{track?.artist ?? '—'}</p>
          <input
            type="range"
            min={0}
            max={duration || 1}
            step="0.1"
            value={Math.min(currentTime, duration || 1)}
            disabled={!available || !duration}
            onChange={(event) => seek(Number(event.target.value))}
            aria-label={locale === 'zh-CN' ? '播放进度' : 'Playback progress'}
            className="music-progress mt-3 block h-px w-full cursor-pointer disabled:cursor-not-allowed"
          />
          <div className="mt-2 flex justify-between font-mono text-[8px] tabular-nums text-ink/50"><span>{formatTime(currentTime)}</span><span>{formatTime(duration)}</span></div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-5">
        <button type="button" onClick={() => moveTrack(-1)} disabled={!available} aria-label={locale === 'zh-CN' ? '上一首' : 'Previous track'} className="text-ink/72 transition hover:text-moss disabled:opacity-30">
          <SkipBack size={14} fill="currentColor" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => void togglePlayback()}
          disabled={!available}
          aria-label={playing ? (locale === 'zh-CN' ? '暂停' : 'Pause') : (locale === 'zh-CN' ? '播放' : 'Play')}
          className="grid size-9 shrink-0 place-items-center rounded-full bg-ink text-white transition duration-200 hover:bg-moss disabled:cursor-not-allowed disabled:opacity-35"
        >
          {playing ? <Pause size={13} fill="currentColor" aria-hidden="true" /> : <Play size={13} fill="currentColor" aria-hidden="true" />}
        </button>
        <button type="button" onClick={() => moveTrack(1)} disabled={!available} aria-label={locale === 'zh-CN' ? '下一首' : 'Next track'} className="text-ink/72 transition hover:text-moss disabled:opacity-30">
          <SkipForward size={14} fill="currentColor" aria-hidden="true" />
        </button>
      </div>

      {available && (
        <>
          <div className="mt-4 flex items-center gap-2 border-t border-ink/12 pt-3">
            <Volume2 size={12} className="shrink-0 text-ink/55" aria-hidden="true" />
            <input type="range" min={0} max={1} step={0.05} value={volume} onChange={(event) => setVolume(Number(event.target.value))} aria-label={locale === 'zh-CN' ? '音量' : 'Volume'} className="music-progress block h-px min-w-0 flex-1 cursor-pointer" />
          </div>
          <ol className="mt-3 border-t border-ink/12">
            {tracks.map((item, index) => {
              const active = index === currentIndex
              return (
                <li key={item.src} className="border-b border-ink/10">
                  <button type="button" onClick={() => selectTrack(index)} aria-current={active ? 'true' : undefined} className={`grid w-full grid-cols-[1.5rem_minmax(0,1fr)] items-center py-2 text-left transition ${active ? 'text-moss' : 'text-ink/65 hover:text-moss'}`}>
                    <span className="font-mono text-[8px] tabular-nums">{String(index + 1).padStart(2, '0')}</span>
                    <span className="truncate text-[10px] font-medium">{item.title}</span>
                  </button>
                </li>
              )
            })}
          </ol>
        </>
      )}
    </section>
  )
}
